import { useEffect, useState } from 'react';
import { supabase } from '../supabase';

export interface OrgProject {
  id: string;
  name: string;
}

/**
 * Lightweight list of the org's active (non-archived) projects, for project
 * pickers in the work-item and document editors. Deliberately NOT useProjectsList
 * — that one computes per-project progress / DoD stats we don't need here.
 */
export function useOrgProjects(
  orgId: string | null
): { projects: OrgProject[]; loading: boolean; error: string | null } {
  const [projects, setProjects] = useState<OrgProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) {
      setProjects([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const { data, error: e } = await supabase
        .from('projects')
        .select('id, name')
        .eq('org_id', orgId)
        .is('archived_at', null)
        .order('name', { ascending: true });
      if (cancelled) return;
      if (e) {
        setError(e.message);
        setProjects([]);
      } else {
        setProjects(
          ((data ?? []) as { id: string; name: string }[]).map((p) => ({
            id: p.id,
            name: p.name || 'Untitled project'
          }))
        );
      }
      setLoading(false);
    })().catch((err) => {
      if (cancelled) return;
      setError(err instanceof Error ? err.message : 'Failed to load projects');
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  return { projects, loading, error };
}
